/** Miesiąc liczymy jako 30 dni. Stawkę pokazujemy między miesiącem a rokiem. */
const MONTH_LENGTH_DAYS = 30;
const MIN_DAYS_FOR_MONTHLY_RATE = 40;
const MAX_DAYS_FOR_MONTHLY_RATE = 365;
const MONTH_PASS_MIN_DAYS = 28;
const MONTH_PASS_MAX_DAYS = 35;

const isWithinMonthlyRange = (days: number): boolean =>
  days >= MIN_DAYS_FOR_MONTHLY_RATE && days <= MAX_DAYS_FOR_MONTHLY_RATE;

export const getMonthlyPassRate = (price: number, dayValidate: number): number | null => {
  const days = Number(dayValidate);
  const amount = Number(price);
  if (!Number.isFinite(days) || !isWithinMonthlyRange(days)) return null;
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return (amount * MONTH_LENGTH_DAYS) / days;
};

/**
 * Cena karnetu na ok. 1 miesiąc z tej samej listy.
 * To punkt odniesienia „gdyby kupować co miesiąc”, a nie zniżka z cennika.
 */
export const getMonthPassPrice = (passes: { price: number; dayValidate: number }[]): number | null => {
  const monthPasses = passes
    .map((pass) => ({ price: Number(pass.price), days: Number(pass.dayValidate) }))
    .filter(
      (pass) =>
        Number.isFinite(pass.price) &&
        pass.price > 0 &&
        Number.isFinite(pass.days) &&
        pass.days >= MONTH_PASS_MIN_DAYS &&
        pass.days <= MONTH_PASS_MAX_DAYS,
    );

  if (monthPasses.length === 0) return null;

  monthPasses.sort((a, b) => {
    const distance = Math.abs(a.days - MONTH_LENGTH_DAYS) - Math.abs(b.days - MONTH_LENGTH_DAYS);
    if (distance !== 0) return distance;
    return a.price - b.price;
  });

  return monthPasses[0].price;
};

/**
 * Najtańsza stawka miesięczna z listy, gdy jest niższa od najtańszego karnetu.
 * Zwraca null, gdy nie da się jej policzyć dla każdego karnetu albo nic nie wychodzi taniej.
 */
export const getCheapestConfidentMonthlyRate = (passes: { price: number; dayValidate: number }[]): number | null => {
  if (passes.length === 0) return null;

  const normalized = passes.map((pass) => ({
    price: Number(pass.price),
    days: Number(pass.dayValidate),
  }));

  const everyPassIsMeasurable = normalized.every(
    (pass) => Number.isFinite(pass.price) && pass.price > 0 && Number.isFinite(pass.days) && pass.days > 0,
  );
  if (!everyPassIsMeasurable) return null;

  const cheapestSticker = Math.min(...normalized.map((pass) => pass.price));
  const monthlyRates = normalized.flatMap((pass) => {
    if (pass.days >= MONTH_PASS_MIN_DAYS && pass.days <= MONTH_PASS_MAX_DAYS) {
      return [pass.price];
    }
    if (!isWithinMonthlyRange(pass.days)) return [];
    return [(pass.price * MONTH_LENGTH_DAYS) / pass.days];
  });

  if (monthlyRates.length === 0) return null;

  const cheapestMonth = Math.min(...monthlyRates);
  if (cheapestMonth >= cheapestSticker - 0.009) return null;

  return Math.round(cheapestMonth * 100) / 100;
};

/** Ile procent taniej wychodzi miesiąc względem kupowania karnetu miesięcznego. */
export const getSavingsPercent = (monthlyRate: number, monthPassPrice: number): number | null => {
  if (!Number.isFinite(monthlyRate) || !Number.isFinite(monthPassPrice) || monthPassPrice <= 0) return null;
  if (monthlyRate >= monthPassPrice) return null;
  const percent = Math.round(((monthPassPrice - monthlyRate) / monthPassPrice) * 100);
  return percent >= 1 ? percent : null;
};
